import { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addISODateDays,
  addJalaliMonths,
  formatJalaliDate,
  formatJalaliMonth,
  jalaliToISO,
  jalaliWeekdayIndex,
  jalaaliMonthDays,
  parseJalaliDate,
  toJalaliParts,
  toPersianDigits,
} from '@/lib/format';
import './JalaliDatePicker.css';

const WEEKDAYS = [
  { short: 'ش', full: 'شنبه' },
  { short: 'ی', full: 'یکشنبه' },
  { short: 'د', full: 'دوشنبه' },
  { short: 'س', full: 'سه‌شنبه' },
  { short: 'چ', full: 'چهارشنبه' },
  { short: 'پ', full: 'پنجشنبه' },
  { short: 'ج', full: 'جمعه' },
];

const todayISO = () => {
  const now = new Date();
  const parts = toJalaliParts(now);
  return parts ? jalaliToISO(parts.jy, parts.jm, parts.jd) : '';
};

const dateIsOutsideBounds = (iso, min, max) => (
  Boolean((min && iso < min) || (max && iso > max))
);

const monthViewFromISO = (iso) => {
  const parts = toJalaliParts(iso);
  return parts ? { jy: parts.jy, jm: parts.jm } : null;
};

const dateToISO = (jy, jm, jd) => jalaliToISO(jy, jm, jd) ?? '';
const dateOnlyISO = (value) => {
  const parts = toJalaliParts(value);
  return parts ? jalaliToISO(parts.jy, parts.jm, parts.jd) : '';
};

const inputValidityMessage = (draft, required, min, max) => {
  if (!draft.trim()) return required ? 'وارد کردن تاریخ شمسی الزامی است.' : '';
  const iso = parseJalaliDate(draft);
  if (!iso) return 'تاریخ شمسی معتبر را وارد کنید.';
  return dateIsOutsideBounds(iso, min, max)
    ? 'تاریخ انتخاب‌شده خارج از بازهٔ مجاز است.'
    : '';
};

export default function JalaliDatePicker({
  value = '',
  onChange,
  className = '',
  label,
  min,
  max,
  rangeStart,
  rangeEnd,
  disabled = false,
  required = false,
  id,
  name,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}) {
  const generatedId = useId();
  const inputId = id || `jalali-date-${generatedId}`;
  const calendarId = `${inputId}-calendar`;
  const errorId = `${inputId}-error`;
  const normalizedValue = dateOnlyISO(value);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const dayRef = useRef(null);
  const focusDayOnOpen = useRef(false);
  const today = todayISO();
  const [draft, setDraft] = useState(normalizedValue ? formatJalaliDate(normalizedValue) : '');
  const [activeISO, setActiveISO] = useState(normalizedValue || today);
  const [view, setView] = useState(() => monthViewFromISO(normalizedValue || today));
  const [previousValue, setPreviousValue] = useState(normalizedValue);
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);

  if (previousValue !== normalizedValue) {
    const nextISO = normalizedValue || today;
    setPreviousValue(normalizedValue);
    setDraft(normalizedValue ? formatJalaliDate(normalizedValue) : '');
    setActiveISO(nextISO);
    setView(monthViewFromISO(nextISO));
  }

  const parsedDraft = parseJalaliDate(draft);
  const hasInvalidDraft = Boolean(draft.trim() && !parsedDraft);
  const outsideBounds = Boolean(parsedDraft && dateIsOutsideBounds(parsedDraft, min, max));
  const missingRequired = required && touched && !draft.trim();
  const invalid = hasInvalidDraft || outsideBounds || missingRequired;
  const validationMessage = missingRequired
    ? 'وارد کردن تاریخ شمسی الزامی است.'
    : outsideBounds
      ? 'تاریخ انتخاب‌شده خارج از بازهٔ مجاز است.'
      : 'تاریخ شمسی را به‌شکل ۱۴۰۵/۰۱/۰۱ وارد کنید.';

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setCustomValidity(inputValidityMessage(draft, required, min, max));
    }
  }, [draft, min, max, required]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open && focusDayOnOpen.current) {
      dayRef.current?.focus();
      focusDayOnOpen.current = false;
    }
  }, [activeISO, open, view]);

  const updateActiveDate = (nextISO) => {
    if (!nextISO) return;
    setActiveISO(nextISO);
    setView(monthViewFromISO(nextISO));
  };

  const openCalendar = () => {
    const nextISO = normalizedValue || parsedDraft || today;
    updateActiveDate(nextISO);
    focusDayOnOpen.current = true;
    setOpen(true);
  };

  const handleInputChange = (event) => {
    const nextDraft = event.target.value;
    setDraft(nextDraft);
    event.currentTarget.setCustomValidity(inputValidityMessage(nextDraft, required, min, max));
    if (!nextDraft.trim()) {
      onChange?.('');
      setActiveISO(today);
      setView(monthViewFromISO(today));
      return;
    }
    const nextISO = parseJalaliDate(nextDraft);
    if (nextISO && !dateIsOutsideBounds(nextISO, min, max)) {
      onChange?.(nextISO);
      updateActiveDate(nextISO);
    }
  };

  const handleInputBlur = () => {
    setTouched(true);
    if (!draft.trim()) {
      setDraft('');
      return;
    }
    if (parsedDraft && !dateIsOutsideBounds(parsedDraft, min, max)) {
      setDraft(formatJalaliDate(parsedDraft));
    }
  };

  const selectDate = (iso) => {
    if (!iso || dateIsOutsideBounds(iso, min, max)) return;
    setDraft(formatJalaliDate(iso));
    onChange?.(iso);
    setOpen(false);
    inputRef.current?.focus();
  };

  const moveMonth = (amount) => {
    if (!view) return;
    const nextISO = addJalaliMonths(activeISO || today, amount);
    const nextView = nextISO ? monthViewFromISO(nextISO) : null;
    if (!nextView) return;
    setView(nextView);
    if (nextISO) setActiveISO(nextISO);
    focusDayOnOpen.current = true;
  };

  const handleDayKeyDown = (event, dayISO, weekdayIndex) => {
    let delta = 0;
    if (event.key === 'ArrowLeft') delta = 1;
    if (event.key === 'ArrowRight') delta = -1;
    if (event.key === 'ArrowDown') delta = 7;
    if (event.key === 'ArrowUp') delta = -7;
    if (event.key === 'Home') delta = -weekdayIndex;
    if (event.key === 'End') delta = 6 - weekdayIndex;

    if (delta) {
      event.preventDefault();
      updateActiveDate(addISODateDays(dayISO, delta));
      focusDayOnOpen.current = true;
      return;
    }

    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      moveMonth((event.key === 'PageUp' ? -1 : 1) * (event.shiftKey ? 12 : 1));
    }
  };

  const gridCells = (() => {
    if (!view) return [];
    const firstDayISO = dateToISO(view.jy, view.jm, 1);
    const offset = jalaliWeekdayIndex(firstDayISO) ?? 0;
    const daysInMonth = jalaaliMonthDays(view.jy, view.jm);
    return Array.from({ length: 42 }, (_, index) => {
      const day = index - offset + 1;
      if (day < 1 || day > daysInMonth) return null;
      return {
        day,
        iso: dateToISO(view.jy, view.jm, day),
        weekdayIndex: index % 7,
      };
    });
  })();

  const monthTitle = view
    ? formatJalaliMonth(dateToISO(view.jy, view.jm, 1))
    : '';
  const inputLabel = ariaLabel || label || 'انتخاب تاریخ شمسی';

  return (
    <div className="jalali-picker" ref={rootRef}>
      {label && <label className="jalali-picker__label" htmlFor={inputId}>{label}</label>}
      <div className="jalali-picker__control">
        <input
          aria-describedby={[ariaDescribedBy, invalid ? errorId : null].filter(Boolean).join(' ') || undefined}
          aria-invalid={invalid || undefined}
          aria-label={inputLabel}
          aria-required={required || undefined}
          autoComplete="off"
          className={`jalali-picker__input ${className}`.trim()}
          disabled={disabled}
          id={inputId}
          inputMode="numeric"
          lang="fa"
          maxLength={10}
          name={name}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onInvalid={(event) => event.currentTarget.setCustomValidity(
            inputValidityMessage(draft, required, min, max),
          )}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'Enter') {
              event.preventDefault();
              openCalendar();
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder="۱۴۰۵/۰۱/۰۱"
          ref={inputRef}
          required={required}
          type="text"
          value={draft}
        />
        <button
          aria-controls={calendarId}
          aria-expanded={open}
          aria-label={open ? 'بستن تقویم' : inputLabel}
          className="jalali-picker__toggle"
          disabled={disabled}
          onClick={() => (open ? setOpen(false) : openCalendar())}
          type="button"
        >
          <CalendarDays aria-hidden="true" size={17} />
        </button>
      </div>
      {invalid && (
        <p className="jalali-picker__error" id={errorId} role="alert">
          {validationMessage}
        </p>
      )}
      {open && view && (
        <div
          aria-label="تقویم شمسی"
          className="jalali-picker__calendar"
          dir="rtl"
          id={calendarId}
          role="dialog"
        >
          <div className="jalali-picker__header">
            <button
              aria-label="ماه قبل"
              className="jalali-picker__month-button"
              onClick={() => moveMonth(-1)}
              type="button"
            >
              <ChevronRight aria-hidden="true" size={18} />
            </button>
            <h2 aria-live="polite" className="jalali-picker__month-title">{monthTitle}</h2>
            <button
              aria-label="ماه بعد"
              className="jalali-picker__month-button"
              onClick={() => moveMonth(1)}
              type="button"
            >
              <ChevronLeft aria-hidden="true" size={18} />
            </button>
          </div>
          <div aria-label={monthTitle} className="jalali-picker__grid" role="grid">
            <div className="jalali-picker__week" role="row">
              {WEEKDAYS.map((weekday) => (
                <span
                  aria-label={weekday.full}
                  className={`jalali-picker__weekday${weekday.full === 'جمعه' ? ' is-weekend' : ''}`}
                  key={weekday.full}
                  role="columnheader"
                  title={weekday.full}
                >
                  {weekday.short}
                </span>
              ))}
            </div>
            {Array.from({ length: 6 }, (_, weekIndex) => (
              <div className="jalali-picker__week" key={weekIndex} role="row">
                {gridCells.slice(weekIndex * 7, (weekIndex + 1) * 7).map((cell, dayIndex) => {
                  if (!cell) {
                    return <span aria-hidden="true" className="jalali-picker__empty" key={`empty-${weekIndex}-${dayIndex}`} role="gridcell" />;
                  }
                  const selected = cell.iso === normalizedValue;
                  const inRange = Boolean(
                    rangeStart && rangeEnd
                    && cell.iso >= rangeStart
                    && cell.iso <= rangeEnd,
                  );
                  const isDisabled = dateIsOutsideBounds(cell.iso, min, max);
                  const weekday = WEEKDAYS[cell.weekdayIndex];
                  const classes = [
                    'jalali-picker__day',
                    selected ? 'is-selected' : '',
                    cell.iso === today ? 'is-today' : '',
                    cell.weekdayIndex === 6 ? 'is-weekend' : '',
                    inRange ? 'is-in-range' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <span className="jalali-picker__cell" key={cell.iso} role="gridcell">
                      <button
                        aria-current={cell.iso === today ? 'date' : undefined}
                        aria-label={`${weekday.full} ${formatJalaliDate(cell.iso, { style: 'long' })}`}
                        aria-pressed={selected}
                        className={classes}
                        disabled={isDisabled}
                        onClick={() => selectDate(cell.iso)}
                        onKeyDown={(event) => handleDayKeyDown(event, cell.iso, cell.weekdayIndex)}
                        ref={cell.iso === activeISO ? dayRef : undefined}
                        tabIndex={cell.iso === activeISO ? 0 : -1}
                        type="button"
                      >
                        {toPersianDigits(cell.day)}
                      </button>
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
